"use client";

import type { NearbyOrganization } from "@homeinstead/marketing-api";
import type { TextStandardInput } from "./form-types";
import type { StandardInputErrors } from "./standard-input-config";

import { useOfficeSearch } from "@homeinstead/geolocation/hooks";
import { getPostalCodeFormatErrorMessage } from "@homeinstead/locale";
import { mapNearbyOrganizationsToOfficeOptions } from "@homeinstead/marketing-api";
import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@homeinstead/ui/components/Select";
import { useMachine } from "@xstate/react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { LeadFormErrorInline } from "./LeadFormErrorInline";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";
import { createOfficeSearchMachine } from "./office-search-machine";
import { isPostalCodeFormatValid } from "./postal-code-config";
import { resolveLabel, resolvePlaceholder } from "./standard-input-config";

interface PostalCodeFieldProps {
  input: TextStandardInput;
  errors: StandardInputErrors;
  onPostalLocationChange?: (values: {
    postalCode: string;
    location: string;
  }) => void;
  /**
   * Persists the selected office id (organization number) so analytics can read
   * it back from persistedFieldValues. Tracked under the "franchiseNumber" key.
   */
  onFieldChange?: (name: string, value: string) => void;
}

function PostalCodeField({
  input,
  errors,
  onPostalLocationChange,
  onFieldChange,
}: PostalCodeFieldProps) {
  const { context } = useLocale();
  const { persistedFieldValues, postalLocationValues } =
    useLeadFormPersistedValues();
  const placeholder = resolvePlaceholder(input);
  const fieldErrors = errors?.postalCode ?? [];
  const locationFieldErrors = errors?.location ?? [];
  const hasError = fieldErrors.length > 0;
  const hasLocationError = locationFieldErrors.length > 0;
  const uid = useId();
  const errorId = `${uid}-error`;
  // Frozen at construction via useState's lazy initializer, not useMemo —
  // useMemo is only a performance hint (see
  // https://react.dev/reference/react/useMemo#caveats) and isn't guaranteed
  // to run once, so it can't be trusted to freeze this restored value.
  const restoredFranchiseNumber = persistedFieldValues.franchiseNumber;
  const [machine] = useState(() =>
    createOfficeSearchMachine({
      preferredOfficeId: restoredFranchiseNumber
        ? Number(restoredFranchiseNumber) || null
        : null,
      typedLocation: postalLocationValues.location,
    }),
  );
  const [snapshot, send] = useMachine(machine);
  const { typedLocation } = snapshot.context;
  const isRestorePending = snapshot.context.preferredOfficeId != null;
  const lastEmittedRef = useRef<{
    franchiseNumber: string;
    location: string;
    postalCode: string;
  } | null>(
    isRestorePending
      ? {
          franchiseNumber: restoredFranchiseNumber ?? "",
          location: postalLocationValues.location,
          postalCode: postalLocationValues.postalCode,
        }
      : null,
  );

  const hasTypedPostalCode = typedLocation.trim() !== "";
  const isFormatValid = isPostalCodeFormatValid(typedLocation, context.code);
  const showFormatError = hasTypedPostalCode && !isFormatValid;

  const {
    data: officeSearch,
    error,
    isFetching,
  } = useOfficeSearch({
    enabled: isFormatValid,
    location: typedLocation,
  });

  const hasLookupError = Boolean(error);

  const officeOptions = useMemo(() => {
    if (hasLookupError) {
      return [];
    }
    const organizations = (officeSearch?.organizations ??
      []) as NearbyOrganization[];
    return mapNearbyOrganizationsToOfficeOptions(organizations);
  }, [hasLookupError, officeSearch?.organizations]);

  useEffect(() => {
    send({
      hasLookupError,
      isFetching,
      options: officeOptions,
      type: "SYNC_LOOKUP",
    });
  }, [hasLookupError, isFetching, officeOptions, send]);

  const selectedOption = snapshot.context.options.find(
    (option) => option.optionId === snapshot.context.selectedOptionId,
  );
  // Keep parity with legacy OfficeStep semantics:
  // - location: user's typed postal input
  // - postalCode: selected office postal code (empty until an office is chosen)
  const finalLocation = typedLocation;
  const selectedPostalCode = selectedOption?.address.postalCode ?? "";
  const selectedFranchiseNumber = selectedOption
    ? String(selectedOption.officeID)
    : "";
  const shouldShowPostalCodeError =
    (hasError && !hasTypedPostalCode) || showFormatError;
  const postalCodeErrorMessage = showFormatError
    ? getPostalCodeFormatErrorMessage(context.code)
    : fieldErrors[0];
  const shouldShowOfficeSelect =
    isFormatValid && !snapshot.context.hasLookupError;
  const isOfficeSelectVisible =
    shouldShowOfficeSelect && !isFetching && officeOptions.length > 0;
  // Re-check current selection rather than trusting the (possibly stale)
  // `errors` prop: once an office auto-selects, any earlier "select a
  // location" error from a prior Continue click no longer applies.
  const hasSelectedOffice = Boolean(selectedOption);

  const emitChange = useCallback(
    (nextValues: {
      franchiseNumber: string;
      location: string;
      postalCode: string;
    }) => {
      const previous = lastEmittedRef.current;
      if (
        previous?.location === nextValues.location &&
        previous?.postalCode === nextValues.postalCode &&
        previous?.franchiseNumber === nextValues.franchiseNumber
      ) {
        return;
      }

      lastEmittedRef.current = nextValues;
      onPostalLocationChange?.({
        location: nextValues.location,
        postalCode: nextValues.postalCode,
      });
      onFieldChange?.("franchiseNumber", nextValues.franchiseNumber);
    },
    [onFieldChange, onPostalLocationChange],
  );

  const handleType = (value: string) => {
    send({ type: "TYPE", value });
    // Clear any previously selected office while the user searches anew.
    emitChange({
      franchiseNumber: "",
      location: value,
      postalCode: "",
    });
  };

  const handleSelectOption = (optionId: string) => {
    const selected = officeOptions.find(
      (option) => option.optionId === optionId,
    );
    // Ignore ids outside the current option set so a stale or empty value
    // emitted while the options are mid-swap cannot clear a valid selection.
    if (!selected) {
      return;
    }

    send({ optionId, type: "SELECT_OPTION" });
    emitChange({
      franchiseNumber: String(selected.officeID),
      location: typedLocation,
      postalCode: selected.address.postalCode,
    });
  };

  // Reconcile parent state when async lookup changes invalidate selection.
  // Guarded while a restore is pending: without this, the effect would fire
  // on mount — before the async lookup resolves — with an empty
  // selectedFranchiseNumber/selectedPostalCode, wiping the machine's restored
  // postalCode/franchiseNumber during that window. Once the lookup resolves,
  // isRestorePending clears and this effect re-runs with the real result.
  useEffect(() => {
    if (isRestorePending) {
      return;
    }

    if (hasLookupError) {
      emitChange({
        franchiseNumber: "",
        location: finalLocation,
        postalCode: "",
      });
      return;
    }

    emitChange({
      franchiseNumber: selectedFranchiseNumber,
      location: finalLocation,
      postalCode: selectedPostalCode,
    });
  }, [
    emitChange,
    finalLocation,
    hasLookupError,
    isRestorePending,
    selectedFranchiseNumber,
    selectedPostalCode,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label className="sr-only" htmlFor={uid}>
          {resolveLabel(input)}
        </label>
        <Input
          aria-describedby={shouldShowPostalCodeError ? errorId : undefined}
          aria-invalid={shouldShowPostalCodeError || undefined}
          autoComplete="postal-code"
          className={cn({ "border-red-500": shouldShowPostalCodeError })}
          id={uid}
          onChange={(event) => handleType(event.currentTarget.value)}
          placeholder={placeholder}
          type="text"
          value={typedLocation}
        />
        {shouldShowPostalCodeError ? (
          <LeadFormErrorInline id={errorId}>
            {postalCodeErrorMessage}
          </LeadFormErrorInline>
        ) : null}
      </div>
      <div className="flex min-h-[76px] flex-col gap-1">
        {shouldShowOfficeSelect ? (
          <p className={cn("min-h-5 text-sm", !isFetching && "invisible")}>
            Loading offices…
          </p>
        ) : (
          <div className="min-h-5" />
        )}
        {isOfficeSelectVisible ? (
          <Select
            onValueChange={handleSelectOption}
            value={snapshot.context.selectedOptionId ?? undefined}
          >
            <SelectTrigger label="Office">
              <SelectValue placeholder="Select office" />
            </SelectTrigger>
            <SelectContent>
              {officeOptions.map((option) => (
                <SelectItem key={option.optionId} value={option.optionId}>
                  {option.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : shouldShowOfficeSelect &&
          !isFetching &&
          officeOptions.length === 0 &&
          !error ? (
          <p className="text-error text-sm">No offices found.</p>
        ) : (
          <div className="h-10" />
        )}
        {hasLocationError && isOfficeSelectVisible && !hasSelectedOffice ? (
          <LeadFormErrorInline>{locationFieldErrors[0]}</LeadFormErrorInline>
        ) : null}
      </div>
    </div>
  );
}

export { PostalCodeField };
